import {
    activityRecordTypeForGoalMetricItemLinkKind,
    marketingGoalRecentActivityLimit,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferRouterOutputs } from "@trpc/server";
import {
    ActivityFeedRowBody,
    ActivityRowParentLink,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import { feedItemDisplay } from "components/details-page-v-next/recent-activities-feed";
import React from "react";
import { goalMetricItemHref } from "./goal-metric-item";

type RecentActivity = inferRouterOutputs<AppRouter>["marketingGoal"]["myRecentActivity"];
type RecentActivityItem = RecentActivity[number];

interface RecentActivityListProps {
    recent: RecentActivity;
}

const rowParentLink = (item: RecentActivityItem): React.ReactNode => {
    if (item.redaction === "deleted") {
        return <span className="text-xs italic text-gray-500">Deleted record</span>;
    }
    if (item.redaction === "otherOffice") {
        return <span className="text-xs italic text-gray-500">In another office</span>;
    }
    if (item.link && item.parentName) {
        return (
            <ActivityRowParentLink
                href={goalMetricItemHref(item.link)}
                parentName={item.parentName}
                recordType={activityRecordTypeForGoalMetricItemLinkKind[item.link.kind]}
            />
        );
    }
    if (item.parentName) {
        return <span className="text-xs text-gray-500">{item.parentName}</span>;
    }
    return null;
};

export const RecentActivityRow: React.FC<{ item: RecentActivityItem }> = ({ item }) => (
    <ActivityFeedRowBody
        display={{ ...feedItemDisplay(item), link: rowParentLink(item) }}
        actions={null}
    />
);

export const RecentActivityList: React.FC<RecentActivityListProps> = ({ recent }) => (
    <section>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Recent activity contributing to goals
        </div>
        {recent.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm text-gray-600">
                No completed visits or outreach recorded for this month.
            </div>
        ) : (
            <>
                <ActivityRowsList
                    className="rounded-lg bg-white px-3 ring-1 ring-gray-200 sm:px-4"
                    testId="goal-recent-activity">
                    {recent.map(item => (
                        <li key={item.id}>
                            <RecentActivityRow item={item} />
                        </li>
                    ))}
                </ActivityRowsList>
                {recent.length >= marketingGoalRecentActivityLimit ? (
                    <div className="mt-1.5 text-xs text-gray-500">
                        Showing the {marketingGoalRecentActivityLimit} most recent
                        activities.
                    </div>
                ) : null}
            </>
        )}
    </section>
);
